/**
 * Japanese and English throughout.
 *
 * The client is Japanese and the drawings are Japanese; an English-only surface
 * would be a demo of something other than the product. Terminology follows the
 * project glossary so wording never drifts between the documents and the tool.
 */
export type Lang = 'ja' | 'en';

export const STRINGS = {
  title: { en: 'PDF → DXF', ja: 'PDF → DXF' },
  subtitle: {
    en: 'Recognition and structuring for engineering drawings',
    ja: '図面の認識と構造化',
  },
  privacy: {
    en: 'Runs entirely in your browser. Your drawing is never uploaded.',
    ja: 'すべてブラウザ内で処理されます。図面がアップロードされることはありません。',
  },
  drop: { en: 'Drop a PDF drawing here', ja: 'PDF 図面をここにドロップ' },
  or: { en: 'or choose a sample', ja: 'またはサンプルを選択' },
  sampleA4: { en: 'A4, 1:1, Japanese', ja: 'A4・1:1・日本語' },
  sampleA3: { en: 'A3, offset crop box', ja: 'A3・クロップボックスあり' },
  sampleRotated: { en: 'A4, rotated 90°', ja: 'A4・90° 回転' },
  browse: { en: 'Choose a file', ja: 'ファイルを選択' },

  /* Input validation — what is wrong with the drawing before it is converted. */
  checkTitle: { en: 'The drawing we were given', ja: 'お預かりした図面について' },
  checkReady: {
    en: 'Nothing to report. This drawing carries what a conversion needs.',
    ja: '報告事項はありません。変換に必要な情報が揃っています。',
  },
  checkDegraded: {
    en: 'This converts. {n} things about it are named below, and what they cost is missing from the result.',
    ja: '変換は可能です。以下に {n} 件を挙げており、それらに相当する情報は結果に含まれません。',
  },
  checkFound: { en: 'What was found', ja: '検出内容' },
  checkRefused: {
    en: 'This cannot be converted. What is below is the reason, not a guess.',
    ja: '変換できません。以下はその理由であり、推測ではありません。',
  },
  checkMeasured: { en: 'Measured', ja: '計測結果' },
  checkCause: { en: 'Why', ja: '原因' },
  checkFix: { en: 'What to do', ja: '対応方法' },
  checkPage: { en: 'Page', ja: 'ページ' },
  checkSeverityBlocking: { en: 'no conversion', ja: '変換不可' },
  checkSeverityDegrading: { en: 'converts, with loss', ja: '変換可・情報欠落' },
  checkSeverityAdvisory: { en: 'converts, be aware', ja: '変換可・注意' },
  checkReExport: {
    en: 'Exporting this drawing again with the settings above would change the result. Send the new file and we will read it instead.',
    ja: '上記の設定で図面を再出力すると結果が変わります。新しいファイルをお送りいただければ、そちらを読み取ります。',
  },
  checkReExportNo: {
    en: 'Exporting again would not change this. It is a property of the drawing rather than of the export.',
    ja: '再出力しても結果は変わりません。出力設定ではなく図面自体の性質です。',
  },
  checkUpload: { en: 'Upload the re-exported drawing', ja: '再出力した図面をアップロード' },

  scale: { en: 'Physical scale', ja: '尺度' },
  scaleUnknown: { en: 'Not determined', ja: '未確定' },
  scaleNeverGuess: {
    en: 'Scale is never guessed. Without it the DXF is refused — a DXF at the wrong scale is worse than none.',
    ja: '尺度は推測しません。確定できない場合、DXF は出力されません。誤った尺度の DXF は、出力しないより悪いためです。',
  },
  paper: { en: 'Sheet', ja: '用紙' },
  drawingScale: { en: 'Drawing scale', ja: '図面尺度' },
  apply: { en: 'Resolve scale', ja: '尺度を確定' },
  calibrate: { en: 'Calibrate by hand', ja: '手動キャリブレーション' },

  /* One step, asking for the one thing the file cannot supply. */
  stepDxf: { en: 'DXF', ja: 'DXF' },
  dxfReady: { en: 'Ready to download', ja: 'ダウンロードできます' },
  dxfDownload: { en: 'Download DXF', ja: 'DXF をダウンロード' },
  dxfNeedsScale: {
    en: 'One thing is needed first: what scale the drawing is at.',
    ja: 'あと1つ必要です。図面の尺度をお知らせください。',
  },
  sheetIs: { en: 'This sheet measures', ja: 'この用紙の実寸' },
  sheetUnrecognised: {
    en: 'not a standard ISO A size',
    ja: 'ISO A 標準サイズではありません',
  },
  sheetMeasuredNote: {
    en: 'Measured from the page itself, so you are not asked for it. A PDF point is exactly 1/72 inch.',
    ja: 'ページ自体から実測しているため、入力は不要です。PDF の 1 ポイントは正確に 1/72 インチです。',
  },
  drawnAtScale: { en: 'The drawing on it is at', ja: '図面の尺度' },
  produceAt: { en: 'Produce the DXF at {scale}', ja: '{scale} で DXF を作成' },
  changeScale: { en: 'Change the scale', ja: '尺度を変更' },
  drawingSays: { en: 'This drawing says', ja: 'この図面の記載' },
  useThis: { en: 'Use it', ja: 'これを使う' },
  scaleAgrees: { en: '— matches your selection', ja: '— 選択と一致しています' },
  whatIsScale: { en: 'What does drawing scale mean?', ja: '図面尺度とは' },
  scaleMeaning: {
    en: 'It is the ratio between the drawing and the real part, printed in the title block next to SCALE or 尺度.',
    ja: '図面と実物の比率です。表題欄の SCALE または尺度の欄に記載されています。',
  },
  scaleOneToOne: {
    en: '1:1 — drawn at actual size. A 50 mm hole is 50 mm on the paper.',
    ja: '1:1 — 実寸で描かれています。50 mm の穴は紙の上でも 50 mm です。',
  },
  scaleOneToTwo: {
    en: '1:2 — drawn at half size. A 100 mm part takes up 50 mm of paper.',
    ja: '1:2 — 半分の大きさで描かれています。100 mm の部品が紙の上では 50 mm です。',
  },
  scaleTwoToOne: {
    en: '2:1 — drawn at twice size, for something small.',
    ja: '2:1 — 2倍の大きさで描かれています。小さな部品向けです。',
  },
  scaleWhyAsk: {
    en: 'It is not read automatically, even when the sheet prints it. A wrong scale produces a DXF that is wrong everywhere and looks right, so a person confirms it.',
    ja: '記載があっても自動では採用しません。尺度を誤ると、正しく見えるのに全体が誤った DXF になるため、必ず人が確認します。',
  },
  orMeasure: {
    en: 'Or measure it: click two points on the drawing whose real distance you know.',
    ja: 'または実測できます。実距離が分かっている2点を図面上でクリックしてください。',
  },

  /* Which font the DXF asks for. See components/DxfFont.tsx for why this is a
     choice rather than a cleverer default. */
  dxfFontLabel: { en: 'Japanese font in the DXF', ja: 'DXF の日本語フォント' },
  dxfOutlineLabel: {
    en: 'Also draw the text as outlines',
    ja: '文字をアウトライン（線分）としても描画する',
  },
  /**
   * States the mechanism, the cost and the limit in three clauses, because every
   * one of them is something the operator would otherwise discover later.
   */
  dxfOutlineNote: {
    en: 'Adds every string a second time as line-work on a TEXT_OUTLINE layer, for viewers that ignore the font setting entirely and show ?? whatever it is set to. The real text is kept, so CAD users can still select and edit it. Downloads a 1.5 MB font the first time, and makes the DXF roughly twenty times larger.',
    ja: 'フォント設定を一切読まず、どの設定でも ?? と表示されるビューア向けに、各文字列を TEXT_OUTLINE レイヤー上の線分としてもう一度描画します。本来の文字要素は残るため、CAD では引き続き選択・編集できます。初回のみ 1.5 MB のフォントを取得し、DXF のサイズはおよそ 20 倍になります。',
  },
  dxfOutlineMissing: {
    en: '{count} character(s) are outside the bundled font and were not outlined: {characters}. They remain in the file as text.',
    ja: '{count} 文字が同梱フォントに含まれないためアウトライン化されていません：{characters}。これらはテキストとしてファイル内に残ります。',
  },
  dxfFontWhy: { en: 'Why the font setting matters', ja: 'フォント設定が効く理由' },
  dxfOutlineWhy: { en: 'What outlines cost', ja: 'アウトライン化の代償' },
  dxfFontNote: {
    en: 'A DXF names a font; it cannot carry one — your CAD application supplies the letter shapes. Pick one your reader has. If it still shows ??, that reader has no Japanese font: many lightweight online viewers ignore this setting entirely and always show ??. AutoCAD and BricsCAD honour it.',
    ja: 'DXF はフォントを指定するだけで、フォント自体は含められません。文字の形はお使いの CAD が用意します。環境にあるフォントを選んでください。それでも ?? と表示される場合、そのビューアに日本語フォントがありません。簡易的なオンラインビューアはこの指定自体を無視し、常に ?? と表示します。AutoCAD や BricsCAD では正しく表示されます。',
  },
  fontWhereJapaneseWindows: { en: 'Japanese Windows', ja: '日本語版 Windows' },
  fontWhereWindows10: { en: 'Windows 10 and later', ja: 'Windows 10 以降' },
  fontWhereWindowsVista: { en: 'Windows Vista and later', ja: 'Windows Vista 以降' },
  fontWhereMacOS: { en: 'macOS', ja: 'macOS' },
  fontWhereNoto: { en: 'Linux, and most online viewers', ja: 'Linux・多くのオンラインビューア' },
  fontWhereOlderAutocad: { en: 'older Japanese AutoCAD', ja: '旧バージョンの日本語 AutoCAD' },
  fontWhereUnknown: { en: 'unknown', ja: '不明' },
  fontStrokePair: { en: 'txt.shx with a big font', ja: 'txt.shx + 大フォント' },

  detailsRead: { en: 'What was read', ja: '読み取り結果' },
  detailsQuality: { en: 'Quality', ja: '品質' },
  detailsHow: { en: 'How it was read', ja: '読み取り方法' },
  detailsFiles: { en: 'Files', ja: 'ファイル' },
  entitiesTotal: { en: '{n} entities', ja: '{n} 要素' },
  issuesTotal: { en: '{n} to review', ja: '確認事項 {n} 件' },
  noneToReview: { en: 'nothing to review', ja: '確認事項なし' },
  calibrateHint: {
    en: 'Click two points on the drawing, then enter the real distance between them.',
    ja: '図面上の2点をクリックし、実際の距離を入力してください。',
  },
  realDistance: { en: 'Real distance (mm)', ja: '実距離 (mm)' },
  reset: { en: 'Start over', ja: 'やり直す' },
  /**
   * Not "Start over": that named the software's state rather than what happens,
   * and it sat at the bottom of the evidence column where the state being named
   * was the one the reader was in the middle of inspecting.
   */
  loadAnother: { en: 'Use a different drawing', ja: '別の図面を読み込む' },
  /** Two files, so there is no single picker to open and the wording says so. */
  comparePairAgain: { en: 'Compare a different pair', ja: '別の組み合わせを比較' },

  entities: { en: 'Entities', ja: '要素' },
  qualityIssues: { en: 'Quality issues', ja: '品質問題' },
  noIssues: { en: 'None recorded', ja: 'なし' },
  ledger: { en: 'Operator ledger', ja: 'オペレータ台帳' },
  identity: { en: 'Every entity, followed through', ja: '各要素の追跡結果' },
  identityOk: {
    en: 'Every entity in the structured output is in the DXF, as itself: none missing, none added, none under another type.',
    ja: '構造化データの全要素が、そのままの型で DXF に含まれています。欠落・追加・型の変化はありません。',
  },
  identityFaults: {
    en: '{n} entities did not arrive as themselves.',
    ja: '{n} 件の要素が、そのままの形で出力されていません。',
  },
  identityMissing: { en: 'missing', ja: '欠落' },
  identityExtra: { en: 'not declared', ja: '未宣言' },
  identityTyped: { en: 'type changed', ja: '型の変化' },
  identityPage: { en: 'wrong page', ja: 'ページ不一致' },
  identityWhyLabel: { en: 'Why a count cannot answer this', ja: '件数では判定できない理由' },
  identityWhy: {
    en: 'Counts cannot answer this. An entity that leaves as a LINE and arrives as an LWPOLYLINE is one entity, correct in every coordinate, and a per-type figure reports it as one lost and one invented.',
    ja: '件数では判定できません。LINE として出力された要素が LWPOLYLINE として書き出された場合、座標はすべて正しいまま 1 要素の型のみが変化していますが、型別の集計では「1 件欠落・1 件増加」として現れます。',
  },
  ledgerExplain: {
    en: 'Every operator in the PDF is either converted into an entity or recorded as a quality issue. Nothing disappears uncounted.',
    ja: 'PDF 内のすべてのオペレータは、要素に変換されるか品質問題として記録されます。数えられずに失われるものはありません。',
  },
  operators: { en: 'operators read', ja: '読み取ったオペレータ' },
  stateOps: { en: 'state, drew nothing', ja: '状態設定（描画なし）' },
  consumed: { en: 'converted', ja: '変換' },
  recorded: { en: 'recorded', ja: '記録' },
  unaccounted: { en: 'unaccounted', ja: '未計上' },

  artefacts: { en: 'Artefacts', ja: '成果物' },
  download: { en: 'Download', ja: 'ダウンロード' },
  dxfRefused: { en: 'DXF refused', ja: 'DXF 出力を拒否' },
  overlay: { en: 'Overlay', ja: 'オーバーレイ' },
  showOverlay: { en: 'Show overlay', ja: 'オーバーレイ表示' },
  showIssues: { en: 'Show issues', ja: '品質問題を表示' },
  page: { en: 'Page', ja: 'ページ' },

  working: { en: 'Converting…', ja: '変換中…' },
  elapsed: { en: 'Converted in', ja: '変換時間' },
  /**
   * True on all three surfaces. "Conversion failed" is the convert surface's
   * sentence — a scan was never being converted, and in both cases the file
   * could not be read, which is also the earlier and more accurate statement.
   */
  failed: { en: 'The file could not be read', ja: 'ファイルを読み取れませんでした' },

  findingCircle: {
    en: 'A PDF has no circle or arc primitive. These were recognised from the canonical four-curve construction; anything else is recorded rather than fitted.',
    ja: 'PDF には円・円弧のプリミティブがありません。標準的な4本のベジェ曲線構成から認識しています。該当しない曲線は近似せず記録します。',
  },
  findingText: {
    en: 'A PDF has no text entities either. One visible line arrives as several runs, split by font changes, and is reassembled here.',
    ja: 'PDF にはテキスト要素もありません。1行が複数のランに分割されて届くため、ここで再構成しています。',
  },
  findingOutlined: {
    en: 'This drawing appears to carry text drawn as outlines, which cannot be read as characters. Reported rather than silently scored as zero.',
    ja: 'アウトライン化された文字が含まれている可能性があります。文字として読み取れないため、無言でゼロ評価とせず報告しています。',
  },

  /* The comparison surface. */
  modeConvert: { en: 'Convert', ja: '変換' },
  modeCompare: { en: 'Compare', ja: '比較' },
  modeScan: { en: 'Scan', ja: 'スキャン' },

  /*
   * The scan surface.
   *
   * Every string here is doing the same job: saying what a scan is worth without
   * either overclaiming or refusing to answer. `docs/HANDOFF.md` — "do not let the
   * demo imply scans are solved" — and the first-use register's closing line, that
   * a number the interface presents wrongly is worse than one it does not present.
   */
  scanTitle: { en: 'Read a scanned drawing', ja: 'スキャン図面を読み取る' },
  scanIntro: {
    en: 'A scan has no geometry in it, only pixels. What comes out is recognised, not read, and every entity carries how sure the detector was.',
    ja: 'スキャンには図形データがなく、画素しかありません。出力は読み取りではなく認識の結果であり、各エンティティに確信度が付きます。',
  },
  scanDrop: { en: 'Drop a scanned PDF here', ja: 'スキャンした PDF をここにドロップ' },
  sampleScan: { en: 'Image-only sheet, 300 DPI', ja: '画像のみの図面・300 DPI' },
  sampleHybrid: { en: 'Hybrid: vector sheet, scanned lower half', ja: 'ハイブリッド：下半分がスキャン' },
  scanWorking: { en: 'Reading the scan. A 600 DPI sheet takes a few seconds.', ja: 'スキャンを読み取っています。600 DPI では数秒かかります。' },

  scanResolution: { en: 'Resolution', ja: '解像度' },
  scanResolutionNote: {
    en: 'A choice, not a property of the file. Higher is slower and not always better.',
    ja: 'ファイルの属性ではなく選択です。高いほど遅く、必ずしも良い結果とは限りません。',
  },
  scanSkew: { en: 'Skew found', ja: '検出された傾き' },
  scanSkewNote: {
    en: 'Measured and reported, not corrected: correcting it would move the geometry off the sheet it is drawn over.',
    ja: '計測して表示するのみで、補正はしません。補正すると図形が背景の図面とずれるためです。',
  },
  /* `scanInk` stood here and was removed with the facts row that used it. The
     ink stamp states the same quantity as an exact count, and a percentage of
     all pixels beside a count of ink pixels reads as a contradiction. */
  scanDetected: { en: 'Detected', ja: '検出' },
  scanConfidence: { en: 'Confidence', ja: '確信度' },
  scanNothingFound: {
    en: 'Nothing was detected. That is an answer, not an error — this detector finds long straight lines, and this sheet has none it recognised.',
    ja: '何も検出されませんでした。これはエラーではなく結果です。この検出器は長い直線を探しますが、認識できるものがありませんでした。',
  },

  scanMerge: { en: 'Merged with the vector layer', ja: 'ベクターレイヤーとの統合' },
  scanMergeNote: {
    en: 'Where both exist, the exact geometry wins. A confident estimate is still an estimate.',
    ja: '両方が存在する場合は正確な図形が優先されます。確信度が高くても推定は推定です。',
  },
  scanMergeVector: { en: 'vector, kept', ja: 'ベクター（採用）' },
  scanMergeRaster: { en: 'raster, added', ja: 'ラスター（追加）' },
  scanMergeSuppressed: { en: 'raster duplicates, dropped', ja: 'ラスター重複（破棄）' },
  scanMergeConflicts: { en: 'disagreements, both kept', ja: '不一致（両方保持）' },

  scanIssues: { en: 'Flagged for review', ja: '確認が必要な項目' },
  scanNoIssues: { en: 'Nothing flagged', ja: '該当なし' },

  /*
   * The ink stamp.
   *
   * Every string here is written fresh rather than shared with the operator
   * ledger above, and the reason is the unit. That stamp counts PDF operators
   * and this one counts ink pixels; a reader who has seen the first would read
   * `27,431` as a number of operators unless the words themselves say otherwise.
   * So the labels name the pixel, and the note says in as many words that a scan
   * carries no operators at all.
   */
  inkLedger: { en: 'Ink ledger', ja: 'インク台帳' },
  inkTotal: { en: 'ink pixels', ja: 'インク画素' },
  inkExplained: { en: 'explained', ja: '説明済み' },
  inkRecorded: { en: 'recorded', ja: '記録済み' },
  inkUnaccounted: { en: 'unaccounted', ja: '未計上' },
  /**
   * One note, and one is the budget.
   *
   * `Ledger.tsx` carries exactly one and the scan surface is held to two screens
   * by `e2e/quality-floor.spec.ts` — a floor that exists because the first
   * person to use the hosted build found both surfaces over two screens with
   * the outcome last on the page (U5). This said the same things in three
   * paragraphs and took the page to 2.38 screens, so it says them in one.
   *
   * Nothing load-bearing was dropped to fit. The share explained still carries
   * the sentence saying it is not a score, because a number this surface
   * presents without that sentence is worse than one it does not present.
   *
   * What did go is the clause explaining that these are pixels rather than PDF
   * operators. The two stamps live on different surfaces and a reader never has
   * both in front of them, and `inkTotal` already labels the column `ink
   * pixels`. The distinction still matters and is written down where it is
   * load-bearing: `InkLedger.tsx`, ADR 0010 and the README.
   */
  inkUnexplained: { en: 'Unexplained ink', ja: '未説明のインク' },
  inkAllExplained: {
    en: 'Every ink pixel is accounted for by a detection.',
    ja: 'すべてのインク画素が検出により説明されています。',
  },
  inkUnexplainedNote: {
    en: 'Where the ink is, not what it is. Most of this is text and arcs, which this detector does not attempt — it is listed so that nothing on the sheet goes uncounted, not because each entry needs a decision.',
    ja: 'インクの位置を示すもので、内容を判定したものではありません。大半は本検出器が対象としない文字と円弧です。数え落としがないことを示すための一覧であり、1件ごとの判断を求めるものではありません。',
  },
  inkRegion: {
    en: '{pixels} px at [{box}] pt',
    ja: '{pixels} px ・ [{box}] pt',
  },
  inkRegionBeside: {
    en: '{pixels} px at [{box}] pt, beside {ids}',
    ja: '{pixels} px ・ [{box}] pt ・ {ids} の隣',
  },
  inkDiffuse: {
    en: '{regions} marks smaller than {floor} pt across, {pixels} px in all. On a degraded scan this is most likely sensor speckle; it is counted, not classified.',
    ja: '{floor} pt 未満の小さな痕跡が {regions} 件、合計 {pixels} px。劣化したスキャンではセンサーノイズの可能性が高いものですが、分類はせず計上のみ行います。',
  },
  /**
   * The note when there was no ink to explain — its own string, and it has to
   * be.
   *
   * It was the first sentence of `inkLedgerExplain`, taken with `split('.')`.
   * A sentence boundary is not a character a component can find: Japanese ends
   * a sentence with `。`, so the split returned the whole two-sentence string
   * and a Japanese reader was told the share explained was `{fraction}` — the
   * literal placeholder, unfilled — on a sheet with no ink to explain.
   */
  inkLedgerNothing: {
    en: 'Every ink pixel is either explained by a detection or recorded as unexplained ink. This sheet carries no ink to account for.',
    ja: 'すべてのインク画素は、いずれかの検出で説明されるか、未説明のインクとして記録されます。この図面には計上すべきインクがありません。',
  },
  inkLedgerExplain: {
    en: 'Every ink pixel is either explained by a detection or recorded as unexplained ink. Detections explain {fraction}, which is not a score: more text on a drawing lowers it.',
    ja: 'すべてのインク画素は、いずれかの検出で説明されるか、未説明のインクとして記録されます。説明できた割合は {fraction} ですが、これは精度ではなく、文字の多い図面ほど下がります。',
  },

  scanAccuracyTitle: { en: 'What this is worth', ja: 'この結果の精度' },
  scanAccuracyBody: {
    en: 'Measured on a held-out drawing at all three resolutions, clean and degraded. Worst row of each kind: lines F1 {line} (precision {precision}, recall {recall}); circles F1 {circle}; frame and title block F1 {polyline}, on two shapes a sheet. Bars: {cleanBar} clean, {noisyBar} degraded. Published accuracy for scanned-drawing recognition is {low}–{high}.',
    ja: '3 解像度・クリーン／劣化の各条件でホールドアウト図面を計測。種類ごとの最低値：直線 F1 {line}（精度 {precision}、再現率 {recall}）、円 F1 {circle}、図枠と表題欄 F1 {polyline}（1 枚あたり 2 図形）。基準はクリーン {cleanBar}、劣化 {noisyBar}。スキャン図面認識の公表精度は {low}〜{high} です。',
  },
  /**
   * The caveat beside a figure of 1.000, and it is the more important of the two
   * this surface has ever carried. A number this high on a corpus this small will
   * be read further than it earns unless the sentence that bounds it sits next to
   * it, so the corpus size, its synthetic origin and the worst UNGATED row are all
   * here rather than in a report nobody opens.
   */
  scanAccuracyGap: {
    en: 'That figure is measured on {drawings} synthetic drawings, degraded deterministically — no customer drawing is used here or anywhere. Held to every image rather than the held-out ones, the worst row is F1 {worst}. What costs precision is real ink the contract has no entity for, such as a freeform curve, reported as a line through it.',
    ja: 'この数値は決定論的に劣化させた合成図面 {drawings} 枚による計測です（実顧客図面は一切使用していません）。ホールドアウトに限らず全画像で見た場合の最低値は F1 {worst} です。精度を下げているのは、自由曲線のように対応する図形要素が仕様上存在しない実インクを、直線として報告してしまう場合です。',
  },
  scanGateFails: { en: 'Does not meet the acceptance bar', ja: '受入基準を満たしていません' },
  scanGateMeets: {
    en: 'Meets the acceptance bar on the held-out drawing',
    ja: 'ホールドアウト図面で受入基準を満たしています',
  },
  scanReportNote: { en: 'Full report', ja: '詳細レポート' },

  scanNoDxf: { en: 'No DXF from a scan', ja: 'スキャンからは DXF を出力しません' },
  scanNoDxfWhy: {
    en: 'Rasterising resolves no physical scale, and scale is never guessed. A DXF at the wrong scale is worse than none.',
    ja: 'ラスタライズでは尺度が確定せず、尺度は推測しません。誤った尺度の DXF は、出力しないより悪いためです。',
  },
  compareIntro: {
    en: 'Two versions of one drawing. Both are read in this browser and neither is uploaded.',
    ja: '同一図面の2つのバージョンを比較します。どちらもこのブラウザ内で読み取り、アップロードしません。',
  },
  dropBefore: { en: 'Drop the before version here', ja: '変更前のPDFをここにドロップ' },
  dropAfter: { en: 'Drop the after version here', ja: '変更後のPDFをここにドロップ' },
  beforeVersion: { en: 'Before', ja: '変更前' },
  afterVersion: { en: 'After', ja: '変更後' },
  diffVersion: { en: 'Difference', ja: '差分' },
  samplePairs: { en: 'or choose a sample pair', ja: 'またはサンプルの組を選択' },
  samplePairA: { en: 'An ordinary revision', ja: '通常の改訂' },
  samplePairSheets: { en: 'A drawing that lost a sheet', ja: 'ページが削除された図面' },
  comparing: { en: 'Comparing…', ja: '比較中…' },
  compareFailed: { en: 'Comparison failed', ja: '比較に失敗しました' },
  compareElapsed: { en: 'Compared in', ja: '比較時間' },
  awaitingSides: {
    en: 'Both versions are needed before a comparison can be made.',
    ja: '比較には両方のバージョンが必要です。',
  },
  loaded: { en: 'loaded', ja: '読み込み済み' },

  sheet: { en: 'Sheet', ja: 'シート' },
  sheetPair: { en: 'before p{n} → after p{m}', ja: '変更前 p{n} → 変更後 p{m}' },
  sheetNew: { en: 'new sheet, after p{m}', ja: '新規ページ・変更後 p{m}' },
  sheetGone: { en: 'removed sheet, before p{n}', ja: '削除ページ・変更前 p{n}' },
  sheetAbsentBefore: {
    en: 'This sheet does not exist in the before version. It was added.',
    ja: 'このページは変更前のバージョンには存在しません。追加されたページです。',
  },
  sheetAbsentAfter: {
    en: 'This sheet does not exist in the after version. It was removed.',
    ja: 'このページは変更後のバージョンには存在しません。削除されたページです。',
  },
  backdropBefore: { en: 'Backdrop: the before page as rendered.', ja: '背景：変更前ページの描画結果' },
  backdropAfter: { en: 'Backdrop: the after page as rendered.', ja: '背景：変更後ページの描画結果' },

  zoomIn: { en: 'Zoom in', ja: '拡大' },
  zoomOut: { en: 'Zoom out', ja: '縮小' },
  resetView: { en: 'Fit', ja: '全体表示' },
  panHint: {
    en: 'Drag the drawing to pan. All three views share one coordinate system, so they stay registered.',
    ja: 'ドラッグで移動できます。3つの表示は同一座標系のため、常に位置が一致します。',
  },
  monochrome: { en: 'One ink', ja: '単色表示' },
  monochromeHint: {
    en: 'Forces every mark to one ink. Class is still carried by line type, line weight and badge shape, so nothing is lost.',
    ja: 'すべての記号を単色にします。分類は線種・線幅・記号の形で表されるため、情報は失われません。',
  },

  legendTitle: { en: 'Change classes', ja: '変更分類' },
  legendHint: {
    en: 'Each row is a filter. Click to hide or show that class on the drawing.',
    ja: '各行がフィルタです。クリックするとその分類の表示・非表示が切り替わります。',
  },
  patternLabel: { en: 'Line type', ja: '線種' },
  hiddenNow: { en: 'hidden', ja: '非表示' },

  summaryTitle: { en: 'Change summary', ja: '変更サマリ' },
  byClassTitle: { en: 'By class', ja: '分類別' },
  byBasePageTitle: { en: 'By page — before version', ja: 'ページ別 — 変更前' },
  byComparePageTitle: { en: 'By page — after version', ja: 'ページ別 — 変更後' },
  byPageNote: {
    en: 'Given twice on purpose. Once a sheet is inserted or removed the same page number names a different sheet in the two versions.',
    ja: '2つに分けています。ページの追加・削除があると、同じページ番号が2つのバージョンで別のシートを指すためです。',
  },
  ambiguousMarks: { en: 'Ambiguous marks', ja: '判定不能の要素数' },
  ambiguousNote: {
    en: 'A count of marks the system declined to resolve. Never folded into changed.',
    ja: '判定を保留した要素の数です。「変更」に含めることはありません。',
  },
  coverage: { en: 'Coverage', ja: 'カバレッジ' },
  toleranceLabel: { en: 'Matching tolerance', ja: '照合公差' },
  matcherProfile: { en: 'Matcher profile', ja: '照合プロファイル' },
  comparisonSpaceLabel: { en: 'Comparison space', ja: '比較空間' },
  spaceDrawingMm: { en: 'drawing millimetres', ja: '図面ミリメートル' },
  spaceNormalised: { en: 'normalised page units', ja: '正規化ページ単位' },
  unitPageDiagonal: { en: 'page diagonals', ja: 'ページ対角長' },
  entityCounts: { en: 'Entities read', ja: '読み取り要素数' },
  sheetCounts: { en: 'Sheets', ja: 'ページ数' },
  alignmentTitle: { en: 'Sheet alignment', ja: 'ページ対応' },
  similarity: { en: 'similarity', ja: '類似度' },
  margin: { en: 'margin', ja: 'マージン' },
  marginZeroNote: {
    en: 'A margin of zero means the pairing was decided by sheet order rather than by evidence.',
    ja: 'マージンが 0 の対応は、根拠ではなくページ順で決められたことを意味します。',
  },
  bundleMismatch: {
    en: 'The two versions were extracted by different bundles. A bundle difference can manufacture changes nobody made.',
    ja: '2つのバージョンは異なる抽出バンドルで処理されています。バンドルの違いは、実際には行われていない変更を生むことがあります。',
  },

  selectedChange: { en: 'Selected change', ja: '選択した変更' },
  selectHint: {
    en: 'Click a mark on the drawing to see both versions of it.',
    ja: '図面上の記号をクリックすると、両バージョンの内容が表示されます。',
  },
  changeClassLabel: { en: 'Change class', ja: '変更分類' },
  displacementLabel: { en: 'Displacement', ja: '移動量' },
  detailLabel: { en: 'Detail', ja: '詳細' },
  candidatesLabel: { en: 'Equally supported candidates', ja: '同等に支持される候補' },
  notInThisVersion: { en: 'Not present in this version.', ja: 'このバージョンには存在しません。' },
  entityMissing: {
    en: 'The comparison names an entity this version no longer holds.',
    ja: '比較結果が参照する要素が、このバージョンには見つかりません。',
  },
  pageLabel: { en: 'Page', ja: 'ページ' },
  bboxLabel: { en: 'Bounding box', ja: 'バウンディングボックス' },
  propertiesLabel: { en: 'Properties', ja: '属性' },
  geometryLabel: { en: 'Geometry', ja: '幾何' },
  provenanceLabel: { en: 'Provenance', ja: '来歴' },
  typeLabel: { en: 'Type', ja: '種別' },
  layerLabel: { en: 'Layer', ja: 'レイヤ' },
  colourLabel: { en: 'Colour', ja: '色' },
  strokeWidthLabel: { en: 'Stroke width', ja: '線幅' },
  originLabel: { en: 'Origin', ja: '取得元' },
  confidenceLabel: { en: 'Confidence', ja: '信頼度' },
  confidenceNone: { en: 'not applicable to a vector entity', ja: 'ベクタ要素には該当なし' },
  bundleLabel: { en: 'Extractor bundle', ja: '抽出バンドル' },
  scaleStateLabel: { en: 'Scale state', ja: '尺度の状態' },
  librariesLabel: { en: 'Library versions', ja: 'ライブラリ版数' },

  /* The change list: what changed, as a list a reviewer can walk. */
  changesTitle: { en: 'What changed', ja: '変更点' },
  changesCount: { en: '{n} on this sheet', ja: 'このシートに {n} 件' },
  changesPosition: { en: '{i} of {n}', ja: '{n} 件中 {i} 件目' },
  noChanges: { en: 'nothing', ja: 'なし' },
  noChangesNote: {
    en: 'Nothing was added, removed, changed or moved on this sheet.',
    ja: 'このシートには追加・削除・変更・移動はありません。',
  },
  previousChange: { en: '← Previous', ja: '← 前へ' },
  nextChange: { en: 'Next →', ja: '次へ →' },
  clearSelection: { en: 'Clear', ja: '選択解除' },
  movedBy: { en: 'moved {d} {unit}', ja: '{d} {unit} 移動' },
  rivalCount: { en: '{n} equally good candidates', ja: '同等の候補 {n} 件' },
  showUnchanged: { en: 'Also mark what did not change', ja: '変更なしの要素も表示' },
  filtersTitle: { en: 'Marks and filters', ja: '記号とフィルタ' },
  measurementTitle: { en: 'How it was measured', ja: '測定条件' },

  diffArtefacts: { en: 'Comparison artefacts', ja: '比較成果物' },
  entityIdLabel: { en: 'Identifier', ja: '識別子' },
  idCollisionNote: {
    en: 'Identifiers are unique within one version only. The same string names a different entity in the other version, so each side is located by version, page and identifier together.',
    ja: '識別子は1つのバージョン内でのみ一意です。同じ文字列がもう一方のバージョンでは別の要素を指すため、各側はバージョン・ページ・識別子の組で特定します。',
  },
} as const;

export type StringKey = keyof typeof STRINGS;

export function t(key: StringKey, lang: Lang): string {
  return STRINGS[key][lang];
}

/**
 * Substitutes `{name}` placeholders.
 *
 * Counts and ratios belong inside the translated sentence, not concatenated onto
 * the end of it — Japanese puts the counter after the number and English does
 * not, and splicing an English unit into a Japanese sentence has already been a
 * defect here once.
 */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    key in values ? String(values[key]) : `{${key}}`,
  );
}

export const ISSUE_LABELS: Record<string, { en: string; ja: string }> = {
  unsupported_curve: { en: 'Curve not a recognised arc', ja: '認識対象外の曲線' },
  outlined_text: { en: 'Text drawn as outlines', ja: 'アウトライン化された文字' },
  undetermined_scale: { en: 'Physical scale not determined', ja: '尺度が未確定' },
  unsupported_operator: { en: 'Operator not convertible', ja: '変換対象外のオペレータ' },
  invisible_text: { en: 'Invisible text layer', ja: '不可視テキストレイヤ' },
  low_confidence: { en: 'Low confidence', ja: '低信頼度' },
  // Not "curve": the vector path may say curve because a PDF operator says `c`,
  // and a raster detector has ink and no such evidence.
  unrecognised_ink: { en: 'Unexplained ink', ja: '未説明のインク' },
  encrypted: { en: 'Encrypted', ja: '暗号化' },
  split_candidate: { en: 'Possible split', ja: '分割候補' },
  merge_candidate: { en: 'Possible merge', ja: '結合候補' },
};

export const ERROR_LABELS: Record<string, { en: string; ja: string }> = {
  PDF_UNSUPPORTED_MEDIA_TYPE: { en: 'That file is not a PDF', ja: 'PDF ではありません' },
  PDF_ENCRYPTED: { en: 'The PDF is encrypted', ja: 'PDF が暗号化されています' },
  PDF_CORRUPT: { en: 'The PDF cannot be parsed', ja: 'PDF を解析できません' },
  PDF_LIMIT_EXCEEDED: { en: 'Beyond the size or page limit', ja: 'サイズまたはページ数の上限超過' },
  EXTRACTION_TIMEOUT: { en: 'Extraction took too long', ja: '抽出が時間内に完了しませんでした' },
  EXTRACTOR_FAILURE: { en: 'Internal extraction failure', ja: '抽出処理の内部エラー' },
  UNEXPECTED: { en: 'Unexpected error', ja: '予期しないエラー' },

  // A refused pair is a classified refusal like any other, so it reads through
  // the same table rather than arriving as free text nobody translated.
  DIFF_PAIR_CROSS_TENANT: {
    en: 'The two versions belong to different tenants',
    ja: '2つのバージョンは異なるテナントに属しています',
  },
  DIFF_PAIR_UNRELATED_DOCUMENT: {
    en: 'The two versions belong to different documents',
    ja: '2つのバージョンは異なる図面に属しています',
  },
  DIFF_PAIR_ARTEFACT_MISSING: {
    en: 'One side has no built artefact to compare. A missing artefact is not an empty drawing',
    ja: '一方に比較対象の成果物がありません。成果物がないことは、空の図面であることとは異なります',
  },
  DIFF_PAIR_CONTRACT_VERSION_UNSUPPORTED: {
    en: 'A contract version this build does not support',
    ja: 'このビルドが対応していない契約バージョンです',
  },
  DIFF_PAIR_CONTRACT_VERSION_MISMATCH: {
    en: 'The two versions declare different contract versions',
    ja: '2つのバージョンの契約バージョンが一致しません',
  },
};
